import { format, parseISO } from 'date-fns';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { FundFitToolDailyTrend } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface FundFitToolTrendChartProps {
  data: FundFitToolDailyTrend[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  const calls = payload.find((p) => p.dataKey === 'calls')?.value || 0;

  const dataPoint = payload[0] as unknown as {
    payload: { cost: number; fullDate: string };
  };

  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium text-sm mb-2'>{dataPoint.payload.fullDate}</p>
      <div className='space-y-1'>
        <p className='text-sm'>
          <span
            className='inline-block w-3 h-3 rounded-sm mr-2'
            style={{ backgroundColor: '#8b5cf6' }}
          />
          Tool Calls: {formatNumber(calls)}
        </p>
        <p className='text-sm text-muted-foreground'>
          Cost: {formatCurrency(dataPoint.payload.cost)}
        </p>
      </div>
    </div>
  );
};

const FundFitToolTrendChart = ({ data }: FundFitToolTrendChartProps) => {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Daily Tool Usage Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    date: format(parseISO(item.date), 'MMM dd'),
    fullDate: item.date,
    calls: item.tool_count,
    cost: parseFloat(item.total_tool_usd),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-lg'>Daily Tool Usage Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={300}>
          <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='date' tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type='monotone'
              dataKey='calls'
              name='Tool Calls'
              stroke='#8b5cf6'
              fill='#8b5cf6'
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default FundFitToolTrendChart;
